import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {
  JiraPodReport,
  JiraSelectedTicketEntry,
  AnalyzerWeeklyPrompt,
} from '@Models/types';
import { useAuth } from './CognitoAuthProvider';
import { analyzerAPI, jiraAnalyzerReportsAPI } from '@Lib/axios/analyzer';
import showToast from '@Common/toast/showToast';

interface ProjectOption {
  id: string;
  key: string;
  name: string;
}

interface AnalyzerReportsContextProps {
  reports: JiraPodReport[];
  setReports: React.Dispatch<React.SetStateAction<JiraPodReport[]>>;
  selectedTickets: Record<string, JiraSelectedTicketEntry>;
  setSelectedTickets: React.Dispatch<
    React.SetStateAction<Record<string, JiraSelectedTicketEntry>>
  >;
  selectedTicket: JiraPodReport['tickets'][0] | null;
  setSelectedTicket: React.Dispatch<
    React.SetStateAction<JiraPodReport['tickets'][0] | null>
  >;
  loading: boolean;
  tagLoading: boolean;
  searchReportDate: string;
  setSearchReportDate: React.Dispatch<React.SetStateAction<string>>;
  searchTicket: string;
  setSearchTicket: React.Dispatch<React.SetStateAction<string>>;
  handleTagTickets: () => Promise<void>;
  handleManualRefetch: () => Promise<void>;
  fetchReports: () => Promise<void>;
  availableProjects: ProjectOption[];
  selectedProject: string;
  setSelectedProject: React.Dispatch<React.SetStateAction<string>>;
  markReportEvaluated: (week: string, projectKey: string) => void;
  markReportEvaluatedAsync: (
    week: string,
    projectKey: string,
    evaluated: boolean
  ) => Promise<void>;
  evaluationLoading: Record<string, boolean>;
  // Prompts
  prompts: AnalyzerWeeklyPrompt[];
  setPrompts: React.Dispatch<React.SetStateAction<AnalyzerWeeklyPrompt[]>>;
  promptsLoading: boolean;
  fetchPrompts: () => Promise<void>;
  createPrompt: (
    prompt: Omit<
      AnalyzerWeeklyPrompt,
      '_id' | 'authorEmail' | 'dateCreated' | 'isDefault'
    >
  ) => Promise<void>;
  updatePrompt: (
    originalTitle: string,
    originalAuthor: string,
    prompt: Partial<AnalyzerWeeklyPrompt>
  ) => Promise<void>;
  deletePrompt: (promptTitle: string, authorEmail: string) => Promise<void>;
  setDefaultPrompt: (promptTitle: string, authorEmail: string) => Promise<void>;
  selectedPrompt: AnalyzerWeeklyPrompt | null;
  setSelectedPrompt: React.Dispatch<
    React.SetStateAction<AnalyzerWeeklyPrompt | null>
  >;
}

const AnalyzerReportsContext = createContext<
  AnalyzerReportsContextProps | undefined
>(undefined);

export const AnalyzerReportsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [reports, setReports] = useState<JiraPodReport[]>([]);
  const [selectedTickets, setSelectedTickets] = useState<
    Record<string, JiraSelectedTicketEntry>
  >({});
  const [selectedTicket, setSelectedTicket] = useState<
    JiraPodReport['tickets'][0] | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [tagLoading, setTagLoading] = useState(false);
  const [searchReportDate, setSearchReportDate] = useState('');
  const [searchTicket, setSearchTicket] = useState('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [cachedReports, setCachedReports] = useState<
    Record<string, JiraPodReport[]>
  >({});
  const [evaluationLoading, setEvaluationLoading] = useState<
    Record<string, boolean>
  >({});
  const [prompts, setPrompts] = useState<AnalyzerWeeklyPrompt[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [selectedPrompt, setSelectedPrompt] =
    useState<AnalyzerWeeklyPrompt | null>(null);
  const [mappings, setMappings] = useState<ProjectOption[]>([]);

  const { user, logout } = useAuth();
  const analyzer = useMemo(() => analyzerAPI(logout), [logout]);
  const jiraAnalyzer = useMemo(() => jiraAnalyzerReportsAPI(logout), [logout]);

  useEffect(() => {
    const fetchMappings = async () => {
      try {
        const projectsMap = new Map<string, ProjectOption>();
        const mappings = await jiraAnalyzer.getMappings();
        mappings.forEach((mapping) => {
          mapping.projects.forEach((project) => {
            projectsMap.set(project.key, {
              id: project.id,
              key: project.key,
              name: project.name,
            });
          });
        });
        setMappings(Array.from(projectsMap.values()));
      } catch (error) {
        console.error('Failed to fetch mappings:', error);
      }
    };

    fetchMappings();
  }, [jiraAnalyzer]);

  const availableProjects: ProjectOption[] = useMemo(
    () => mappings,
    [mappings]
  );

  useEffect(() => {
    if (availableProjects.length > 0 && !selectedProject) {
      setSelectedProject(availableProjects[0].key);
    }
  }, [availableProjects, selectedProject]);

  useEffect(() => {
    if (selectedProject) {
      fetchReports(selectedProject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const fetchReports = async (projectId?: string) => {
    const project = projectId ?? selectedProject;
    if (!project) return;

    const cached = cachedReports[project];
    if (cached && cached.length > 0) {
      setReports(cached);
      return;
    }

    setLoading(true);
    try {
      const data = await analyzer.getJiraWeeklyReports(project);
      setReports(data);
      setCachedReports((prev) => ({ ...prev, [project]: data }));
    } catch (error) {
      showToast.error('Failed to fetch reports:', error as string);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.admin) {
      fetchPrompts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (prompts.length > 0 && !selectedPrompt) {
      const defaultPrompt = prompts.find((p) => p.isDefault);
      setSelectedPrompt(defaultPrompt || prompts[0]);
    }
  }, [prompts, selectedPrompt]);

  const handleTagTickets = async () => {
    const hasSelected = Object.keys(selectedTickets).length > 0;
    if (!hasSelected) {
      showToast.error('No selected tickets to tag.');
      return;
    }
    try {
      setTagLoading(true);
      await analyzer.tagJiraReports(selectedTickets);
      const updatedReports =
        await analyzer.getJiraWeeklyReports(selectedProject);
      setReports(updatedReports);
      setCachedReports((prev) => ({
        ...prev,
        [selectedProject]: updatedReports,
      }));
      showToast.success('Tagging success!');
      setSelectedTickets({});
    } catch (error) {
      showToast.error('Failed to tag selected tickets:', error as string);
    } finally {
      setTagLoading(false);
    }
  };

  const handleManualRefetch = async () => {
    setLoading(true);
    try {
      const refreshed = await analyzer.getJiraWeeklyReports(selectedProject);
      setReports(refreshed);
      setCachedReports((prev) => ({ ...prev, [selectedProject]: refreshed }));
    } catch (error) {
      showToast.error('Failed to fetch reports:', error as string);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrompts = async () => {
    setPromptsLoading(true);
    try {
      const data = await jiraAnalyzer.getPrompts();
      setPrompts(data);
    } catch (error) {
      showToast.error('Failed to fetch prompts:', error as string);
    } finally {
      setPromptsLoading(false);
    }
  };

  const createPrompt = async (
    prompt: Omit<
      AnalyzerWeeklyPrompt,
      '_id' | 'authorEmail' | 'dateCreated' | 'isDefault'
    >
  ) => {
    try {
      await jiraAnalyzer.createPrompt(prompt);
      await fetchPrompts();
      showToast.success('Prompt created successfully!');
    } catch (error) {
      showToast.error('Failed to create prompt:', error as string);
    }
  };

  const updatePrompt = async (
    originalTitle: string,
    originalAuthor: string,
    prompt: Partial<AnalyzerWeeklyPrompt>
  ) => {
    try {
      await jiraAnalyzer.updatePrompt(originalTitle, originalAuthor, prompt);
      await fetchPrompts();
      showToast.success('Prompt updated successfully!');
    } catch (error) {
      showToast.error('Failed to update prompt:', error as string);
    }
  };

  const deletePrompt = async (promptTitle: string, authorEmail: string) => {
    try {
      await jiraAnalyzer.deletePrompt(promptTitle, authorEmail);
      await fetchPrompts();
      showToast.success('Prompt deleted successfully!');
    } catch (error) {
      showToast.error('Failed to delete prompt:', error as string);
    }
  };

  const setDefaultPrompt = async (promptTitle: string, authorEmail: string) => {
    try {
      await jiraAnalyzer.setDefaultPrompt(promptTitle, authorEmail);
      await fetchPrompts();
      showToast.success('Default prompt set successfully!');
    } catch (error) {
      showToast.error('Failed to set default prompt:', error as string);
    }
  };

  const markReportEvaluatedAsync = async (
    week: string,
    projectKey: string,
    evaluated: boolean
  ) => {
    const key = `${week}_${projectKey}`;
    setEvaluationLoading((prev) => ({ ...prev, [key]: true }));
    try {
      await analyzer.evaluateJiraReport(week, projectKey, evaluated);
      const updateReports = (prev: JiraPodReport[]) =>
        prev.map((report) =>
          report.week === week && report.project.key === projectKey
            ? {
                ...report,
                evaluated,
                evaluatedAt: evaluated ? new Date().toISOString() : undefined,
              }
            : report
        );

      setReports(updateReports);
      setCachedReports((prevCache) => ({
        ...prevCache,
        [projectKey]: updateReports(prevCache[projectKey] || []),
      }));
    } catch (e) {
      // Optionally show toast
    } finally {
      setEvaluationLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const markReportEvaluated = (week: string, projectKey: string) => {
    const report = reports.find(
      (r) => r.week === week && r.project.key === projectKey
    );
    if (report) {
      markReportEvaluatedAsync(week, projectKey, !report.evaluated);
    }
  };

  return (
    <AnalyzerReportsContext.Provider
      value={{
        reports,
        setReports,
        selectedTickets,
        setSelectedTickets,
        selectedTicket,
        setSelectedTicket,
        loading,
        tagLoading,
        searchReportDate,
        setSearchReportDate,
        searchTicket,
        setSearchTicket,
        handleTagTickets,
        handleManualRefetch,
        fetchReports,
        availableProjects,
        selectedProject,
        setSelectedProject,
        markReportEvaluated,
        markReportEvaluatedAsync,
        evaluationLoading,
        prompts,
        setPrompts,
        promptsLoading,
        fetchPrompts,
        createPrompt,
        updatePrompt,
        deletePrompt,
        setDefaultPrompt,
        selectedPrompt,
        setSelectedPrompt,
      }}
    >
      {children}
    </AnalyzerReportsContext.Provider>
  );
};

export const useAnalyzerReportsContext = (): AnalyzerReportsContextProps => {
  const context = useContext(AnalyzerReportsContext);
  if (!context) {
    throw new Error(
      'useAnalyzerReportsContext must be used within an AnalyzerReportsProvider'
    );
  }
  return context;
};
